import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { Cons, firstValueFrom } from 'rxjs';
import {
  Partenaire,
  Produit,
  NatureOperation,
  BackOfficeMember,
  StatutFilterInput,
  Statut,
  FondEvenementiel,
  Investisseur,
  Consultant,
  Commande,
  CommandeFilterInput,
} from '../models/generated/graphql';
import { QueryManagerService } from './queryManagerService';

const INVESTISSEUR_BY_ID = gql`
  query investisseurById($id: Long!) {
    investisseurById(id: $id) {
      id
      code
      creationDate
      statutCode
      statutCodeNavigation {
        libelle
        actif
      }
      investisseurEntite {
        displayName
        personnePhysique {
          dateNaissance
          prenom
          nom
          genre
        }
      }
      investisseurConsultant(where: { actif: { eq: 1 } }) {
        consultant {
          personnePhysique {
            nom
            prenom
          }
        }
      }
    }
  }
`;

const CONSULTANT_BY_ID = gql`
  query consultantById($id: Long!) {
    consultantById(id: $id) {
      id
      personnePhysique {
        nom
        prenom
      }
    }
  }
`;

const GET_COMMANDES_FINANCIERE = gql`
  query commandesFinancieresPaginated($where: CommandeFilterInput) {
    commandesFinancieresPaginated(skip: 0, take: 100, where: $where) {
      items {
        id
        nom
        produitId
        numeroContrat
        dateActe
        produit {
          id
          nom
          nomTechniqueProduitBox
        }
        investisseurCommande {
          investisseurId
          investisseur {
            investisseurEntite {
              displayName
            }
          }
        }
      }
      totalCount
    }
  }
`;

const partenairesQuery = gql`
  query partenaires($filter: PartenaireFilterInput) {
    partenaires(where: $filter) {
      id
      nom
    }
  }
`;

const backOfficeMembers = gql`
  query backOfficeMembers($where: BackOfficeMemberFilterInput) {
    backOfficeMembers(where: $where) {
      id
      tokenId
      displayName
    }
  }
`;

const produits = gql`
  query produits($where: ProduitFilterInput) {
    produits(where: $where) {
      id
      nom
      nomTechniqueProduitBox
      enveloppeNavigation {
        code
        libelle
      }
    }
  }
`;

const natureOperations = gql`
  query natureOperations($where: NatureOperationFilterInput) {
    natureOperations(where: $where) {
      id
      libelle
    }
  }
`;

const statuts = gql`
  query statuts($where: StatutFilterInput) {
    statuts(where: $where) {
      id
      backOfficeLibelle
      consultantLibelle
    }
  }
`;

const PAGEALLACTIVEFONDS = gql`
  query fondEvenementielsPaginated($skip: Int, $take: Int) {
    fondEvenementielsPaginated(skip: $skip, take: $take) {
      pageInfo {
        hasNextPage
      }
      items {
        id
        libelle
      }
      totalCount
    }
  }
`;
const NUMBER_OF_FONDSEVENEMENTIELS_BY_REQUEST = 50;
@Injectable({
  providedIn: 'root',
})
export class DashboardFiltersService {
  private partenaires: Partenaire[] = [];
  private produits: Produit[] = [];
  private natureOperations: NatureOperation[] = [];
  private gestionnaires: BackOfficeMember[] = [];

  private statutsMap: Map<StatutFilterInput, Statut[]> = new Map();
  private allFondsEvenementiels: FondEvenementiel[] = [];
  constructor(private queryManager: QueryManagerService) {}

  private investisseur: Investisseur;

  async getInvestisseurById(investisseurId: number): Promise<Investisseur | undefined> {
    if (!investisseurId) return undefined;
    const result = await firstValueFrom(
      this.queryManager.query<{ investisseurById: Investisseur }>({
        query: INVESTISSEUR_BY_ID,
        variables: {
          id: investisseurId,
        },
        fetchPolicy: 'cache-first',
      })
    );

    return result.data.investisseurById;
  }

  async getConsultantById(consultantId: number): Promise<Consultant | undefined> {
    if (!consultantId) return undefined;
    const result = await firstValueFrom(
      this.queryManager.query<{ consultantById: Consultant }>({
        query: CONSULTANT_BY_ID,
        variables: {
          id: consultantId,
        },
        fetchPolicy: 'cache-first',
      })
    );

    return result.data.consultantById;
  }

  async getCommandeById(contratId: number): Promise<Commande | undefined> {
    if (!contratId) return undefined;
    const result = await firstValueFrom(
      this.queryManager.query<{ commandesFinancieresPaginated: { items: Commande[] } }>({
        query: GET_COMMANDES_FINANCIERE,
        variables: {
          where: { id: { eq: contratId } } as CommandeFilterInput,
        },
        fetchPolicy: 'cache-first',
      })
    );

    return result.data.commandesFinancieresPaginated.items[0];
  }

  async getPartenaires(): Promise<Partenaire[]> {
    if (this.partenaires.length === 0) {
      const activiteFilter = { activiteRattachement: { eq: 0 } }; //  activite investissement only
      const result = await firstValueFrom(
        this.queryManager.query<{ partenaires: Partenaire[] }>(
          {
            query: partenairesQuery,
            variables: {
              filter: activiteFilter,
            },
          },
          false,
          true
        )
      );
      this.partenaires = result.data.partenaires;
    }
    return this.partenaires;
  }

  async getProduits(): Promise<Produit[]> {
    if (this.produits.length === 0) {
      const result = await firstValueFrom(
        this.queryManager.query<{ produits: Produit[] }>(
          {
            query: produits,
          },
          false,
          true
        )
      );
      this.produits = result.data.produits;
    }
    return this.produits;
  }

  async getNatures(): Promise<NatureOperation[]> {
    if (this.natureOperations.length === 0) {
      const result = await firstValueFrom(
        this.queryManager.query<{ natureOperations: NatureOperation[] }>(
          {
            query: natureOperations,
          },
          false,
          true
        )
      );
      this.natureOperations = result.data.natureOperations;
    }
    return this.natureOperations;
  }

  async getGestionnaires(): Promise<BackOfficeMember[]> {
    if (this.gestionnaires.length === 0) {
      const result = await firstValueFrom(
        this.queryManager.query<{ backOfficeMembers: BackOfficeMember[] }>(
          {
            query: backOfficeMembers,
          },
          false,
          true
        )
      );
      this.gestionnaires = result.data.backOfficeMembers;
    }
    return this.gestionnaires;
  }

  async getStatuts(where: StatutFilterInput) {
    if (!this.statutsMap.has(where)) {
      const result = await firstValueFrom(
        this.queryManager.query<{ statuts: Statut[] }>(
          {
            query: statuts,
            variables: {
              where,
            },
          },
          false,
          true
        )
      );
      this.statutsMap.set(where, result.data.statuts);
    }
    return this.statutsMap.get(where);
  }

  private async getPartOfFondsEvenementiels(round: number = 0): Promise<{
    pageInfo: {
      hasNextPage: boolean;
    };
    items: any[];
    totalCount: number;
  }> {
    const result = await firstValueFrom(
      this.queryManager.query<{
        fondEvenementielsPaginated: { pageInfo: { hasNextPage: boolean }; items: any[]; totalCount: number };
      }>({
        query: PAGEALLACTIVEFONDS,
        variables: {
          skip: 0 + NUMBER_OF_FONDSEVENEMENTIELS_BY_REQUEST * round,
          take: NUMBER_OF_FONDSEVENEMENTIELS_BY_REQUEST + NUMBER_OF_FONDSEVENEMENTIELS_BY_REQUEST * round,
        },
      })
    );

    return result.data.fondEvenementielsPaginated;
  }
  private async getAllIterativeLoadedFondsEvenementiels(): Promise<FondEvenementiel[]> {
    let hasNextPage: boolean = false;
    let round = 0;
    let allFondsEvenementiels: any[] = [];
    do {
      const result = await this.getPartOfFondsEvenementiels(round);
      allFondsEvenementiels = allFondsEvenementiels.concat(result.items);
      hasNextPage = result.pageInfo.hasNextPage;
      round++;
    } while (hasNextPage);
    return allFondsEvenementiels;
  }
  //FIXME: this use mulitple paginated requests.this is a temporary solution, not a future proof solution
  async getAllFondsEvenementiels(): Promise<FondEvenementiel[]> {
    if (this.allFondsEvenementiels.length > 0) {
      return this.allFondsEvenementiels;
    }
    this.allFondsEvenementiels = await this.getAllIterativeLoadedFondsEvenementiels();
    return this.allFondsEvenementiels;
  }
}
